import {
  Alert,
  AlertTitle,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useMemo } from "react";

import {
  collectReportWarnings,
  hasResidualWork,
  type PieceBackfillReportView as ReportView,
} from "./piece-backfill-report";

const styles = {
  root: { mt: 2, maxWidth: 900 },
  section: { mt: 2.5 },
  alert: { mb: 2 },
  counts: { mt: 1 },
  notes: { mt: 2 },
} satisfies Record<string, SxProps<Theme>>;

/**
 * Renders a `null` verification count as an explicit "not measured" rather than
 * a zero, since a check that could not run means something different from a
 * check that found nothing.
 *
 * @param value The verification count.
 * @returns The count, or a not-measured marker.
 */
const formatVerificationCount = (value: number | null): string => {
  return value === null ? "not measured" : String(value);
};

/** Props for {@link PieceBackfillReportPanel}. */
export type PieceBackfillReportPanelProps = {
  /** The narrowed report of a finished run. */
  readonly report: ReportView;
  /** True when the run only planned and wrote nothing. */
  readonly isDryRun: boolean;
};

/**
 * Renders a finished run's report: warnings first, then what it planned per
 * billing party, what it actually wrote, and the end-of-run verification.
 *
 * Warnings lead because the backend degrades a failed verification check into a
 * warning and still reports the run COMPLETED, so a buried warning is the one
 * way this tool could quietly mislead an operator.
 */
const PieceBackfillReportPanel = ({
  report,
  isDryRun,
}: PieceBackfillReportPanelProps) => {
  const warnings = useMemo(() => collectReportWarnings(report), [report]);
  const residualWork = useMemo(() => hasResidualWork(report), [report]);
  const { verification } = report;

  return (
    <Box sx={styles.root}>
      {warnings.length === 0 ? null : (
        <Alert severity="warning" sx={styles.alert}>
          <AlertTitle>
            {warnings.length === 1
              ? "1 warning"
              : `${warnings.length} warnings`}
          </AlertTitle>
          {warnings.map((warning) => (
            <Typography key={warning} variant="body2">
              {warning}
            </Typography>
          ))}
        </Alert>
      )}

      <Alert severity={isDryRun ? "info" : "success"}>
        {isDryRun
          ? `Planned ${report.totals.piecesToCreate} new pieces, ${report.totals.statusUpdates} status updates, and ${report.totals.locationUpdates} location fills across ${report.totals.packagesScanned} packages. Nothing was written.`
          : `Created ${report.applied.piecesCreated} pieces and ${report.applied.joinsCreated} package links, bumped ${report.applied.piecesStatusUpdated} statuses, and filled ${report.applied.piecesLocationUpdated} locations.`}
      </Alert>

      {report.totals.packagesSkipped === 0 ? null : (
        <Typography color="text.secondary" variant="body2" sx={styles.counts}>
          {report.totals.packagesSkipped} packages were skipped because they
          already have every piece they need.
        </Typography>
      )}

      {report.billingParties.length === 0 ? null : (
        <Box sx={styles.section}>
          <Typography variant="subtitle2">By billing party</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Billing party</TableCell>
                <TableCell align="right">Packages scanned</TableCell>
                <TableCell align="right">Skipped</TableCell>
                <TableCell align="right">Pieces planned</TableCell>
                <TableCell align="right">Status updates</TableCell>
                <TableCell align="right">Location fills</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.billingParties.map((party) => (
                <TableRow key={party.contactUuid}>
                  <TableCell>{party.contactName}</TableCell>
                  <TableCell align="right">{party.packagesScanned}</TableCell>
                  <TableCell align="right">{party.packagesSkipped}</TableCell>
                  <TableCell align="right">{party.piecesToCreate}</TableCell>
                  <TableCell align="right">{party.statusUpdates}</TableCell>
                  <TableCell align="right">{party.locationUpdates}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {verification === null ? null : (
        <Box sx={styles.section}>
          <Typography variant="subtitle2">Verification</Typography>
          {residualWork ? (
            <Alert severity="warning" sx={styles.counts}>
              Verification still finds work in scope. Re-running is safe and
              idempotent; if a re-run keeps reporting the same residue, the
              remaining rows need a closer look rather than another run.
            </Alert>
          ) : (
            <Alert severity="success" sx={styles.counts}>
              Every in-scope package has pieces, and no piece is behind the
              status its order implies.
            </Alert>
          )}
          <Stack spacing={0.5} sx={styles.counts}>
            <Typography variant="body2">
              In-scope packages:{" "}
              {formatVerificationCount(verification.totalPackages)}
            </Typography>
            <Typography variant="body2">
              Pieces on in-scope orders:{" "}
              {formatVerificationCount(verification.totalPieces)}
            </Typography>
            <Typography variant="body2">
              Packages still without any piece:{" "}
              {formatVerificationCount(verification.packagesWithoutPieces)}
            </Typography>
            <Typography variant="body2">
              Pieces behind their order status:{" "}
              {formatVerificationCount(verification.piecesBehindOrderStatus)}
            </Typography>
          </Stack>

          {verification.statusDistribution === null ? null : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Piece status</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {verification.statusDistribution.map((entry) => (
                  <TableRow key={entry.status}>
                    <TableCell>{entry.status}</TableCell>
                    <TableCell align="right">{entry.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      {report.notes.length === 0 ? null : (
        <Box sx={styles.notes}>
          <Typography variant="subtitle2">Notes</Typography>
          {report.notes.map((note) => (
            <Typography key={note} color="text.secondary" variant="body2">
              {note}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PieceBackfillReportPanel;
